/*
 * This file includes classes and functions which are relevant for the pluralistic-view feature.
 * It is used by both the server and the client.
*/
import type { Post } from "~~/prisma/generated/client"

/* RateLimit implements a simple leaky-bucket ratelimiter.
 *
 * It has many parameter, so here is how to make some sense of it.
 * Let's say there is a scarse resource, and multiple actors want to access it.
 * As an example, the resource could be the network interface, and the actors are different software.
 * Or, in blob, the resource is screen real estate, and the actors are different sources.
 * The idea is that accessing the network "cost" money. And software gets money by waiting.
 * But you can't just wait so much time, get very rich, and then send 9999 packets, because there
 * is a limit for that, too.
 * At every cycle, you can send one packet: who will be the software to actually send it?
 * The one who can afford it!
 * 
 * @param initial   initial money for all queues
 * @param k         money every queue receives at every step
 * @param burst     maximum amount of money anyone can have in their hands
 * @param cost      cost of accessing the scarce resource.
 */
export class RateLimit<T> {
  initial: number
  k: number
  burst: number
  cost: number
  buckets = new Map<T, number>()

  constructor(initial: number, k: number, burst: number, cost: number) {
    this.initial = initial
    this.k = k
    this.burst = burst
    this.cost = cost
  }

  add() {
    for (const s of this.buckets.keys()) {
      this.buckets.set(s, this.buckets.get(s) + this.k)
      if (this.buckets.get(s) > this.burst) {
        this.buckets.set(s, this.burst)
      }
    }
  }

  getBucket(s: T): number {
    if (this.buckets.get(s) === undefined) {
      this.buckets.set(s, this.initial)
    }
    return this.buckets.get(s)
  }

  check(s: T) {
    return this.getBucket(s) >= this.cost
  }

  pop(s: T) {
    if (this.check(s)) {
      // not only we make this bucket pay its cost; we also make it pay the
      // same increment it will get at the next .add() (which the caller is expected to make)
      this.buckets.set(s, this.getBucket(s) - this.cost - this.k)
      return true
    }
    return false
  }
}

export class Shuffler {
  readonly postsQueue: Post[];

  constructor(rateLimiter: RateLimit<number>, reserve: number) {
    // a reserve is useful to make the fairness more effective
    // in fact, with reserve=0, every batch of 12 posts is reshuffled on its own,
    // but no post will go to the next batch.
    // With a larger reserve, this can happen.
    this.rateLimiter = rateLimiter
    this.reserve = reserve
    this.removed = []
    this.postsQueue = []
  }

  add(listOfPosts) {
    if (listOfPosts === undefined) {
      console.error('Shuffler.add(undefined)')
      return
    }
    if (listOfPosts.value !== undefined) {
      listOfPosts = listOfPosts.value
    }
    for (const item of listOfPosts) {
      if (!this.removed.includes(item.id)
        && this.postsQueue.findIndex(p => p.id == item.id) == -1
      ) {
        this.postsQueue.push(item)
      }
    }
  }

  remove(post) {
    this.removed.push(post.id)
    const idx = this.postsQueue.findIndex(p => p.id === post.id)
    if (idx !== -1) {
      this.postsQueue.splice(idx, 1)
    }
    else {
      console.error('Trying to remove a post which has never been added?')
    }
  }

  addAndGet(listOfPosts) {
    this.add(listOfPosts)
    const newPosts = []

    while (this.postsQueue.length > this.reserve && newPosts.length < listOfPosts.length) {
      let idx = 0
      for (const item of this.postsQueue) {
        const source = item.source.id
        if (this.rateLimiter.pop(source)) {
          newPosts.push(item)
          this.remove(item)
          break
        }
        else {
        }
        idx += 1
      }
      this.rateLimiter.add()
    }
    return newPosts
  }
}

